import { useState } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

/**
 * Sortable Table Component
 * 
 * Usage:
 * const { sortedData, sortConfig, handleSort, SortIcon } = useSortableTable(data, 'name')
 * 
 * Then in your table header:
 * <th onClick={() => handleSort('columnKey')}>
 *   Column Name <SortIcon column="columnKey" />
 * </th>
 */

export function useSortableTable(data, defaultSortKey = null, defaultDirection = 'asc') {
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultDirection
  })

  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    // Handle numbers
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
    }

    // Handle strings (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    }

    // Handle booleans
    if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      return sortConfig.direction === 'asc' 
        ? (aValue === bValue ? 0 : aValue ? -1 : 1)
        : (aValue === bValue ? 0 : aValue ? 1 : -1)
    }

    return 0
  })

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ChevronsUpDown className="h-4 w-4 inline ml-1 text-muted-foreground" />
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-4 w-4 inline ml-1" />
      : <ChevronDown className="h-4 w-4 inline ml-1" />
  }

  return { sortedData, sortConfig, handleSort, SortIcon }
}

/**
 * Sortable Table Header Component
 * Provides consistent styling for sortable headers
 */
export function SortableTableHeader({ children, onClick, className = '' }) {
  return (
    <th 
      onClick={onClick}
      className={`cursor-pointer hover:bg-muted/50 transition-colors select-none ${className}`}
    >
      {children}
    </th>
  )
}
